import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Quote, QuoteItem } from '../../types';
import * as quoteService from '../../services/quote-service';
import { RootState } from '../store';
import API_CONFIG from '../../config/api';

interface QuoteState {
  items: Quote[];
  selectedQuote: Quote | null;
  quoteItems: QuoteItem[];
  loading: boolean;
  error: string | null;
}

const initialState: QuoteState = {
  items: [],
  selectedQuote: null,
  quoteItems: [],
  loading: false,
  error: null,
};

export const fetchQuotes = createAsyncThunk<Quote[], string | undefined>(
  'quotes/fetchQuotes',
  async (searchTerm, thunkAPI) => {
    try {
      const response = await quoteService.getQuotes(searchTerm || '');
      if (!response.success) {
        throw new Error('Failed to fetch quotes');
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message);
    }
  }
);

export const fetchQuoteById = createAsyncThunk(
  'quotes/fetchQuoteById',
  async (quoteId: string, { rejectWithValue }) => {
    try {
      const response = await quoteService.getQuoteWithItems(quoteId);
      if (!response.success) {
        throw new Error('Failed to fetch quote details');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchQuoteItems = createAsyncThunk(
  'quotes/fetchQuoteItems',
  async (quoteId: string, { rejectWithValue }) => {
    try {
      const response = await quoteService.getQuoteItems(quoteId);
      if (!response.success) {
        throw new Error('Failed to fetch quote items');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const quoteSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    selectQuote: (state, action: PayloadAction<Quote>) => {
      state.selectedQuote = action.payload;
    },
    clearSelectedQuote: state => {
      state.selectedQuote = null;
      state.quoteItems = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchQuotes.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchQuoteById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedQuote = action.payload;
        if (action.payload.items) {
          state.quoteItems = action.payload.items;
        }
      })
      .addCase(fetchQuoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchQuoteItems.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteItems.fulfilled, (state, action) => {
        state.loading = false;
        state.quoteItems = action.payload;
      })
      .addCase(fetchQuoteItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { selectQuote, clearSelectedQuote } = quoteSlice.actions;

// Selectors
export const selectQuotes = (state: RootState) => state.quotes.items;
export const selectSelectedQuote = (state: RootState) => state.quotes.selectedQuote;
export const selectQuoteItems = (state: RootState) => state.quotes.quoteItems;
export const selectLoading = (state: RootState) => state.quotes.loading;
export const selectError = (state: RootState) => state.quotes.error;

export default quoteSlice.reducer;
